// Indian Rupee + date helpers
//
// Iter 102.2 · Attendance Timezone Cleanup — All timestamp formatters now
// force display in a single deterministic timezone (`DISPLAY_TIMEZONE`).
// The backend stores UTC; the presentation layer converts. When per-user
// timezone support arrives, swap the single constant below.
export const DISPLAY_TIMEZONE = "Asia/Kolkata"; // TODO: swap to user.settings.timezone once multi-country lands
export const DISPLAY_TIMEZONE_LABEL = "IST"; // Short label rendered next to prominent timestamps

const inrFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const inrCompact = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: "compact",
    maximumFractionDigits: 1,
});

export const formatINR = (value, opts) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    const n = Number(value);
    // Iter 49.1 — overloaded 2nd argument:
    //   • string  → treat as ISO 4217 currency code; route to formatMoney
    //   • object  → existing options ({compact:true})
    //   • undef.  → INR full format (legacy default)
    // Back-compat: every existing call site that passes nothing / undefined
    // continues to render INR exactly as before. Operator-facing detail
    // and print pages opt into per-currency rendering by passing the
    // document's `currency` string explicitly.
    if (typeof opts === "string") return formatMoney(n, opts);
    if (opts && opts.compact) return inrCompact.format(n);
    return inrFormatter.format(n);
};

// Iter 49.1 — Native-currency money formatter for documents.
// Cached per-currency formatter (Intl.NumberFormat is expensive to spawn).
// `formatINR` above is preserved for dashboards which intentionally stay
// INR-centric per operator directive (no FX, no mixed-currency aggregation).
const _moneyFormatters = new Map();
const _currencyLocales = {
    INR: "en-IN", USD: "en-US", AED: "en-AE", GBP: "en-GB", EUR: "de-DE",
};
const _currencyFallback = (code) => {
    const c = (code || "INR").toUpperCase();
    return _currencyLocales[c] ? c : "INR";
};
export const formatMoney = (value, currencyCode, { compact = false } = {}) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
    const code = _currencyFallback(currencyCode);
    const key = `${code}:${compact ? "C" : "F"}`;
    let fmt = _moneyFormatters.get(key);
    if (!fmt) {
        fmt = new Intl.NumberFormat(_currencyLocales[code], {
            style: "currency",
            currency: code,
            minimumFractionDigits: compact ? 0 : 0,
            maximumFractionDigits: compact ? 1 : 2,
            ...(compact ? { notation: "compact" } : {}),
        });
        _moneyFormatters.set(key, fmt);
    }
    return fmt.format(Number(value));
};

export const formatDate = (iso, opts = {}) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: DISPLAY_TIMEZONE,
            ...opts,
        });
    } catch {
        return "—";
    }
};

// Iter 102.2 — Time-of-day formatter (12-hour by default, forced to
// DISPLAY_TIMEZONE). Used everywhere in the Attendance module. Callers
// wanting the zone label appended (prominent cards / popups) should use
// `formatTimeIST` instead. Tables should use `formatTime` (bare) and
// include a single "All times are displayed in IST." disclaimer.
export const formatTime = (iso, opts = {}) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const raw = d.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: DISPLAY_TIMEZONE,
            ...opts,
        });
        // en-IN Intl returns lowercase am/pm — force uppercase to match REEDOS spec.
        return raw.replace(/\s*am\b/i, " AM").replace(/\s*pm\b/i, " PM");
    } catch {
        return "—";
    }
};

export const formatTimeIST = (iso, opts = {}) => {
    const t = formatTime(iso, opts);
    return t === "—" ? "—" : `${t} ${DISPLAY_TIMEZONE_LABEL}`;
};

export const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: DISPLAY_TIMEZONE });
        const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: DISPLAY_TIMEZONE });
        // Force uppercase AM/PM.
        return `${date}, ${time.replace(/\s*am\b/i, " AM").replace(/\s*pm\b/i, " PM")}`;
    } catch {
        return "—";
    }
};

export const formatDateTimeIST = (iso) => {
    const t = formatDateTime(iso);
    return t === "—" ? "—" : `${t} ${DISPLAY_TIMEZONE_LABEL}`;
};

export const toISODate = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    return new Date(yyyyMmDd + "T00:00:00.000Z").toISOString();
};

export const dateInputValue = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toISOString().slice(0, 10);
    } catch {
        return "";
    }
};
